import { useEffect, useState } from "react";
import { Navigate, useParams } from "react-router";
import { useNavigate } from "../lib/nav";
import { Button, Logo } from "@omnabase/ui";
import type { Me } from "@omnabase/shared";
import { authClient } from "../lib/auth";
import { signOutHere } from "../lib/push";
import { acceptInvitation, declineInvitation } from "../lib/invitations";
import { joinedToast } from "./signin/SignInSteps";

type Invitation = { id: string; email: string; organizationName: string; organizationSlug: string; inviterEmail: string; role: string; status: string; expiresAt: string };

/** /invite/<id>: the link from the invitation mail. Without a session it sends the person to sign in (the first sign-in creates
 *  their account), and the proof accepts it (M11 round one); with a session it shows who invited them where, and Accept joins the
 *  workspace through our accept route (its refusals in words). Signed in as another address, the library's 403 offers "Sign out and
 *  continue as" the invited address instead of a dead end. */
export function InviteScreen({ me, onJoined }: { me: Me | null; onJoined: () => Promise<void> }) {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const [inv, setInv] = useState<Invitation | null | undefined>(undefined);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [otherAddress, setOtherAddress] = useState(false);
  useEffect(() => {
    if (!me) return;
    authClient.organization.getInvitation({ query: { id } }).then((r) => { if (r.error) { setOtherAddress(r.error.status === 403); setProblem(r.error.message ?? "This invitation is not valid"); setInv(null); } else setInv(r.data as unknown as Invitation); });
  }, [id, me]);
  if (!me) return <Navigate to={`/signin?invite=${encodeURIComponent(id)}`} replace />;
  const accept = async () => {
    setBusy(true);
    try {
      const j = await acceptInvitation(id);
      await onJoined(); joinedToast([j]);
      navigate(`/${j.slug}`, { replace: true });
    } catch (e) { setBusy(false); setProblem(e instanceof Error ? e.message : "Could not accept"); }
  };
  const decline = async () => { await declineInvitation(id).catch(() => undefined); navigate("/", { replace: true }); };
  const continueAsInvited = async () => { await signOutHere(); window.location.assign(`/signin?invite=${encodeURIComponent(id)}`); };
  return (
    <div className="ob-signin">
      <aside className="ob-signin__aside" aria-hidden="true"><div className="ob-signin__aside-inner"><span className="ob-signin__logo"><Logo /></span><h2 className="ob-signin__claim">{inv ? `Join ${inv.organizationName}` : "An invitation"}</h2><p className="ob-signin__line">Your team's support inbox, with an AI that answers first.</p></div></aside>
      <main className="ob-signin__main" aria-label="Invitation">
        <div className="ob-signin__box">
          {inv === undefined && !problem && <p className="ob-signin__intro">Looking up the invitation…</p>}
          {problem && otherAddress && <div className="ob-signin__form"><h1 className="ob-signin__title">This invitation is for another address</h1><p className="ob-signin__intro">You are signed in as <strong>{me.user.email}</strong>. Sign in with the address the invitation went to.</p><Button variant="primary" className="ob-signin__btn" onClick={() => void continueAsInvited()}>Sign out and continue as the invited address</Button><p className="ob-signin__foot"><button type="button" className="ob-link" onClick={() => navigate("/")}>Go to your workspaces</button></p></div>}
          {problem && !otherAddress && <div className="ob-signin__form"><h1 className="ob-signin__title">This invitation cannot be used</h1><p className="ob-signin__intro">{problem}</p><Button onClick={() => navigate("/")}>Go to your workspaces</Button></div>}
          {inv && !problem && (
            <div className="ob-signin__form">
              <h1 className="ob-signin__title">You're invited</h1>
              <p className="ob-signin__intro"><strong>{inv.inviterEmail}</strong> invited <strong>{inv.email}</strong> to join <strong>{inv.organizationName}</strong>.{inv.email.toLowerCase() !== me.user.email.toLowerCase() && <> You are signed in as {me.user.email}: sign out and use the invited address.</>}</p>
              {problem === null && <p className="ob-signin__problem" role="alert" hidden />}
              <Button variant="primary" className="ob-signin__btn" disabled={busy || inv.email.toLowerCase() !== me.user.email.toLowerCase()} onClick={() => void accept()}>Accept and join</Button>
              <p className="ob-signin__foot"><button type="button" className="ob-link" onClick={() => void decline()}>Decline</button></p>
            </div>
          )}
        </div>
        <p className="ob-signin__legal">OmnaBase</p>
      </main>
    </div>
  );
}
